import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import FontBody1 from '../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import Table from '../../../../components/table/table';
import TableRow from '../../../../components/table/components/table-row/table-row';
import IconInfo from '../../../../components/icons/icon-info/icon-info';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import PlanName from '../plan-name/plan-name';
import CurrencyLable from '../currency-lable/currency-lable';
import Card from '../../../../components/layouts/card/card';
import ActionList from '../action-list/action-list';
import AssignSelectedPlanBlock from '../assign-selected-plan-block/assign-selected-plan-block';
import AssignShopsConfirmModal from '../assign-shops-confirm-modal/assign-shops-confirm-modal';
import ShopsAssignedToPlanNotificationModal
    from '../shops-assigned-to-plan-notification-modal/shops-assigned-to-plan-notification-modal';
import Icon from '../../../../components/icons/icon/icon';

import SubscriptionPlanStore from '../../stores/subscription-plan-editor-store';
import SubscriptionPlanPagedListStore from '../../stores/subscription-plan-paged-list-store';
import AssignShopsToPlanStore from "../../stores/assign-shops-to-plan-store";
import FontHeading2 from '../../../../components/fonts/font-heading2/font-heading2';
import { PLAN_TYPES } from '../../../../models/Plan';
import { Button } from '../../../../components/inputs/button/button';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';
import IconPlus from '../../../../components/icons/icon-plus/icon-plus';

interface IProps {
    pagedListStore: SubscriptionPlanPagedListStore;
    type: PLAN_TYPES;
    title?: string
}

const PlansCard: React.FC<IProps> = observer(({ pagedListStore, type, title }) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!pagedListStore.total) {
            pagedListStore.loadPage(1);
        }
    }, [pagedListStore]);

    const onCreateNewClicked = () => {
        navigate({
            pathname: `${type}/new`
        });
    }

    const [assignShopsToPlanStore, setAssignShopsToPlanStore] = useState<AssignShopsToPlanStore | undefined>(undefined);
    const onSelectPlanToAssign = (plan: SubscriptionPlanStore) => {
        const store = new AssignShopsToPlanStore(plan, plan.featuresInfo);
        setAssignShopsToPlanStore(store);
    }

    const [showAssignShopsConfirmModal, setShowAssignShopsConfirmModal] = useState(false);
    const onAssignShopsConfirmModalClose = () => {
        setAssignShopsToPlanStore(undefined);
        setShowAssignShopsConfirmModal(false);
    }

    const onCloseShopsAssignedToPlanNotificationModal = () => {
        setAssignShopsToPlanStore(undefined);
    }

    return (
        <>
            <Card
                disabled={pagedListStore.isLoading}
            >
                {title && <FontHeading2>{title}</FontHeading2>}
                <AssignSelectedPlanBlock
                    store={assignShopsToPlanStore}
                    onAssignShopsClick={setShowAssignShopsConfirmModal}
                />
                <Table
                    selectableRows
                    isLoading={pagedListStore.isLoading}
                    pagination={{
                        onSetPage: pagedListStore.loadPage,
                        page: pagedListStore.page,
                        total: pagedListStore.total,
                        perPage: pagedListStore.perPage
                    }}
                    sort={{
                        onSort: pagedListStore.sortData,
                        by: pagedListStore.sortBy,
                        dir: pagedListStore.sortDir,
                    }}
                    totalResultsTitle='Plans'
                    columns={[
                        {
                            content: 'ID',
                            sortBy: 'id',
                            sortable: true,
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Name',
                            width: {
                                value: 2,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Tag',
                            width: {
                                value: 1.5,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Price',
                            sortBy: 'price',
                            sortable: true,
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'Returns quota',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Excess return price',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Excess billing cap',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Trial days',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Can be assigned',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Assigned stores',
                            width: {
                                value: 1,
                                type: 'fr'
                            },
                        },
                        {
                            content: 'Features',
                            width: {
                                value: 60,
                                type: 'px'
                            }
                        },
                        {
                            content: 'Note',
                            width: {
                                value: 50,
                                type: 'px'
                            }
                        },
                        {
                            content: 'Actions',
                            width: {
                                value: 70,
                                type: 'px'
                            }
                        },
                    ]}
                >
                    {pagedListStore.total > 0 && pagedListStore.list.map((item: SubscriptionPlanStore) => (
                        <TableRow
                            selectable
                            onSelect={() => onSelectPlanToAssign(item)}
                            selected={assignShopsToPlanStore?.plan?.id === item.id}
                            key={item.id}
                            cells={[
                                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    {item.id}
                                </FontBody1>,
                                <PlanName isDefault={item.on_install} isNew={item.isNewlyCreated}>
                                    {item.name}
                                </PlanName>
                                ,
                                item.tag,
                                <CurrencyLable value={item.price} />,
                                item.rma_quota,
                                <CurrencyLable value={item.price_per_extra_rma} />,
                                <CurrencyLable value={item.capped_amount} />,
                                item.trialDays,
                                item.deprecated ? 'No' : 'Yes',
                                item.assigned_stores,
                                item.featuresCount ? (<Tooltip
                                    content={<div style={{ maxWidth: '500px' }}>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{item.featuresInfo}</FontBody2>
                                    </div>}
                                >
                                    {item.featuresCount}
                                </Tooltip>) : item.featuresCount,
                                item.internal_name && <Tooltip
                                    content={<div style={{ maxWidth: '350px' }}>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{item.internal_name}</FontBody2>
                                    </div>}
                                >
                                    <Icon Component={IconInfo} />
                                </Tooltip>,
                                item.id && <ActionList planId={item.id} type={type} />
                            ]}
                            name={type}
                        />
                    ))}
                </Table>
                <div className='add-plan'>
                    <Button
                        variant={BUTTON_VARIANTS.Text}
                        text="add plan"
                        leftIcon={IconPlus}
                        onClick={onCreateNewClicked}
                    />
                </div>
            </Card>
            {
                (
                    showAssignShopsConfirmModal
                    && assignShopsToPlanStore
                    && !assignShopsToPlanStore.assignedShops.length
                )
                && <AssignShopsConfirmModal
                    store={assignShopsToPlanStore}
                    onClose={onAssignShopsConfirmModalClose}
                />
            }
            {
                (!!assignShopsToPlanStore?.assignedShops.length && assignShopsToPlanStore.plan.id)
                && <ShopsAssignedToPlanNotificationModal
                    onClose={onCloseShopsAssignedToPlanNotificationModal}
                    assignedShops={assignShopsToPlanStore?.assignedShops}
                    planId={assignShopsToPlanStore.plan.id}
                />
            }
        </>
    );
});

export default PlansCard;
